import { useState, useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Hook to detect if we should use reduced animations for performance
 * NEW TIERED APPROACH:
 * - shouldDisableHeavyEffects: true on mobile (no particles, blur, infinite loops)
 * - shouldUseLightAnimations: true on mobile (faster, simpler, but still animated!)
 * - shouldDisableAllAnimations: only when user prefers reduced motion
 */
export function useMobilePerformance() {
  const prefersReducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  const [isLowPerfDevice, setIsLowPerfDevice] = useState(false);

  useEffect(() => {
    // Check if mobile via screen width
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check device performance (low memory, slow connection, or high pixel ratio on small screens)
    const checkPerformance = () => {
      const nav = navigator as any;
      const lowMemory = nav.deviceMemory && nav.deviceMemory < 4;
      const slowConnection = nav.connection && 
        (nav.connection.effectiveType === '2g' || nav.connection.effectiveType === 'slow-2g');
      // Only flag as low-perf on very high DPI + tiny screen
      const mobileHighDPI = window.innerWidth < 400 && window.devicePixelRatio > 2.5;
      
      setIsLowPerfDevice(lowMemory || slowConnection || mobileHighDPI);
    };

    checkMobile();
    checkPerformance();
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // TIERED ANIMATION CONTROL:
  
  // Completely disable animations only for users who prefer it
  const shouldDisableAllAnimations = prefersReducedMotion === true;
  
  // Disable HEAVY effects on mobile (particles, blur, infinite loops, mouse-follow)
  const shouldDisableHeavyEffects = isMobile || isLowPerfDevice;
  
  // Use lighter/faster animations on mobile (but still animated!)
  const shouldUseLightAnimations = isMobile && !shouldDisableAllAnimations;
  
  // Legacy alias for backwards compatibility
  const shouldReduceAnimations = shouldDisableAllAnimations || isLowPerfDevice;
  
  // Even more aggressive reduction for blur effects (expensive on mobile GPU)
  const shouldReduceBlur = isMobile || isLowPerfDevice;

  return {
    isMobile,
    isLowPerfDevice,
    prefersReducedMotion,
    // New tiered flags
    shouldDisableAllAnimations,
    shouldDisableHeavyEffects,
    shouldUseLightAnimations,
    // Legacy
    shouldReduceAnimations,
    shouldReduceBlur,
  };
}

/**
 * Utility hook that provides mobile-optimized animation values
 * All animations use ONLY transform and opacity (GPU-accelerated)
 * NOW WITH RICHER MOBILE ANIMATIONS!
 */
export function useMobileAnimationConfig() {
  const { 
    shouldDisableAllAnimations, 
    shouldDisableHeavyEffects, 
    shouldUseLightAnimations,
    shouldReduceBlur, 
    isMobile 
  } = useMobilePerformance();

  // If user prefers no animations, return instant values
  if (shouldDisableAllAnimations) {
    return {
      transition: { duration: 0 },
      fadeIn: { initial: { opacity: 1 }, animate: { opacity: 1 } },
      fadeUp: { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } },
      slideInRight: { initial: { opacity: 1, x: 0 }, animate: { opacity: 1, x: 0 } },
      scaleIn: { initial: { opacity: 1, scale: 1 }, animate: { opacity: 1, scale: 1 } },
      staggerChildren: 0,
      useLayout: false,
      blur: 'blur(0px)',
      tapScale: {},
      hoverScale: {},
      shouldDisableAllAnimations,
      shouldDisableHeavyEffects,
      shouldUseLightAnimations,
      shouldReduceBlur,
      isMobile,
    };
  }

  // MOBILE: SMOOTH and BUTTERY animations (premium feel)
  if (shouldUseLightAnimations) {
    // Premium easing curve - smooth deceleration like iOS
    const smoothEase = [0.32, 0.72, 0, 1]; // Custom bezier for buttery feel
    
    return {
      // Smooth, flowing transitions
      transition: { duration: 0.5, ease: smoothEase },
      
      // Fade up with elegant movement
      fadeIn: { 
        initial: { opacity: 0, y: 20 }, 
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.55, ease: smoothEase }
      },
      
      // Nice fade up for cards - more dramatic
      fadeUp: { 
        initial: { opacity: 0, y: 35 }, 
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: smoothEase }
      },
      
      // Slide from right (RTL-friendly) - elegant slide
      slideInRight: { 
        initial: { opacity: 0, x: 40 }, 
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.55, ease: smoothEase }
      },
      
      // Scale in for buttons/cards - subtle but noticeable
      scaleIn: { 
        initial: { opacity: 0, scale: 0.88 }, 
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.5, ease: smoothEase }
      },
      
      // Stagger for lists - slightly more delay for drama
      staggerChildren: 0.1,
      
      // Skip heavy layout animations
      useLayout: false,
      
      // No blur on mobile
      blur: 'blur(0px)',
      
      // Tap feedback - satisfying press
      tapScale: { scale: 0.96 },
      
      // No hover on touch
      hoverScale: {},
      
      shouldDisableAllAnimations,
      shouldDisableHeavyEffects,
      shouldUseLightAnimations,
      shouldReduceBlur,
      isMobile,
    };
  }

  // DESKTOP: Full rich animations
  return {
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    
    fadeIn: { 
      initial: { opacity: 0, y: 25 }, 
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
    },
    
    fadeUp: { 
      initial: { opacity: 0, y: 40 }, 
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
    },
    
    slideInRight: { 
      initial: { opacity: 0, x: 60 }, 
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
    },
    
    scaleIn: { 
      initial: { opacity: 0, scale: 0.85 }, 
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
    },
    
    staggerChildren: 0.12,
    useLayout: true,
    blur: undefined,
    tapScale: { scale: 0.95 },
    hoverScale: { scale: 1.03 },
    
    shouldDisableAllAnimations,
    shouldDisableHeavyEffects,
    shouldUseLightAnimations,
    shouldReduceBlur,
    isMobile,
  };
}

/**
 * Mobile-optimized animation variants for framer-motion
 * These only animate transform and opacity for 60fps performance
 */
export const mobileAnimationVariants = {
  // Container for staggered children
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  },
  
  // Individual item fade up
  fadeUp: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    },
  },
  
  // Quick fade (for mobile)
  quickFade: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.25 }
    },
  },
  
  // Slide from right (for RTL content)
  slideRight: {
    hidden: { opacity: 0, x: 30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    },
  },
  
  // Scale in (for cards, buttons)
  scaleIn: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
  },
};
